import React, { useState, useEffect } from "react";
import api from "../api";
import ReactGA from "react-ga4";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const selectedPlanId = location.state?.selectedPlanId;

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleError = params.get("error");
    if (googleError) {
      setError(t("login_page.google_auth_failed"));
    }
    const checkAuth = async () => {
      try {
        const res = await api.get("/auth/me", { withCredentials: true });
        if (res.data.authenticated) {
          await onLogin?.();
          if (selectedPlanId) navigate("/checkout", { state: { selectedPlanId } });
          else if (from && from !== "/" && from !== "/login") navigate(from);
          else navigate("/admin/events");
        }
      } catch (err) {}
    };
    checkAuth();
  }, [navigate, location, onLogin, t]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post(
        `/auth/login`,
        { email, password, remember_me: rememberMe },
        { withCredentials: true }
      );
      if (res.data.success) {
        ReactGA.event({ category: 'conversion', action: 'login' });
        await onLogin();
        if (selectedPlanId) navigate("/checkout", { state: { selectedPlanId } });
        else if (from && from !== "/" && from !== "/login") navigate(from);
        else navigate("/admin/events");
      }
    } catch (err) {
      setError(t("login_page.login_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (from && from !== "/" && from !== "/login") sessionStorage.setItem("auth_redirect", from);
    window.location.href = `${api.defaults.baseURL}/auth/google`;
  };

  return (
    <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
      <div style={{ width: "100%", maxWidth: "420px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <img src="/logo_central.png" alt="Pic-Loud Logo" style={{ height: "48px", width: "auto" }} />
        </div>

        {/* Card */}
        <div style={{
          background: "#fff",
          borderRadius: "18px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          padding: "32px 28px",
        }}>
          <h5 style={{ textAlign: "center", fontWeight: 700, color: "#111827", marginBottom: "24px", fontSize: "17px" }}>
            {t("login_page.title")}
          </h5>

          {/* Error */}
          {error && (
            <div style={{
              padding: "10px 14px", borderRadius: "9px",
              background: "#fff5f5", border: "1px solid #fecaca",
              color: "#991b1b", fontSize: "13px", marginBottom: "16px",
            }}>
              {error}
            </div>
          )}

          {/* Google button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            style={{
              width: "100%", height: "44px",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
              borderRadius: "10px", border: "1px solid #e5e7eb",
              background: "#fff", cursor: "pointer",
              fontSize: "14px", fontWeight: 500, color: "#374151",
              transition: "all 0.15s",
              marginBottom: "20px",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "none"; }}
          >
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            {t("login_page.google_button")}
          </button>

          {/* Divider */}
          <div style={{ position: "relative", marginBottom: "20px" }}>
            <div style={{ height: 1, background: "#f3f4f6" }} />
            <span style={{
              position: "absolute", top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff", padding: "0 10px",
              fontSize: "12px", color: "#9ca3af",
            }}>
              {t("login_page.or_text")}
            </span>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
                {t("login_page.email_label")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: "100%", height: "40px",
                  padding: "0 12px", borderRadius: "9px",
                  border: "1px solid #e5e7eb", outline: "none",
                  fontSize: "13px", color: "#374151",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  boxSizing: "border-box",
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "#a5b4fc"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>

            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
                {t("login_page.password_label")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: "100%", height: "40px",
                  padding: "0 12px", borderRadius: "9px",
                  border: "1px solid #e5e7eb", outline: "none",
                  fontSize: "13px", color: "#374151",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  boxSizing: "border-box",
                }}
                onFocus={e => { e.currentTarget.style.borderColor = "#a5b4fc"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
              <input
                type="checkbox"
                id="remember-me-checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ cursor: "pointer", accentColor: "#6366f1" }}
              />
              <label htmlFor="remember-me-checkbox" style={{ fontSize: "12px", color: "#6b7280", cursor: "pointer", marginBottom: 0 }}>
                {t("login_page.remember_me")}
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", height: "44px",
                borderRadius: "10px", border: "none",
                background: loading ? "#a5b4fc" : "#6366f1",
                color: "#fff", fontSize: "14px", fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.15s",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#4f46e5"; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#6366f1"; }}
            >
              {loading ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                  <path d="M12 2a10 10 0 0110 10" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </svg>
              ) : t("login_page.login_button")}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "#6b7280" }}>
            {t("login_page.signup_prompt")}{" "}
            <Link to="/signup" style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}
              onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
              onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
            >
              {t("login_page.signup_link")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
